/**
 * ChainVault WP - Wallet Context
 *
 * This is the global state manager for the wallet.
 * Supports both built-in wallets (seed phrase) and
 * external wallets (MetaMask and Phantom).
 * Built-in wallet ALWAYS takes priority over external wallets.
 * Auto-polls balances every 30 seconds and plays sounds on changes.
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { fetchTransactions } from '../utils/etherscan';
import { fetchAllPrices } from '../utils/prices';
import { playReceiveSound } from '../utils/sounds';
import {
    isPhantomInstalled,
    connectPhantom,
    fetchSolBalance,
    fetchSolTransactions,
    fetchSolTokenBalances,
    listenToPhantomEvents,
} from '../utils/solana';
import {
    loadWalletFromStorage,
    walletExistsInStorage,
    deleteWalletFromStorage,
    sendEthTransaction,
    sendSolTransaction,
} from '../utils/wallet';

const WalletContext = createContext( null );

/* Testnet RPC endpoints — used when testnet mode is enabled */
export const TESTNET_NETWORKS = {
    ethereum: { rpc: 'https://ethereum-sepolia-rpc.publicnode.com', explorer: 'https://sepolia.etherscan.io',   chainId: 11155111, name: 'Ethereum Sepolia',  symbol: 'ETH',  color: '#627EEA' },
    bsc:      { rpc: 'https://data-seed-prebsc-1-s1.binance.org:8545', explorer: 'https://testnet.bscscan.com', chainId: 97,       name: 'BSC Testnet',        symbol: 'tBNB', color: '#F0B90B' },
    polygon:  { rpc: 'https://rpc-amoy.polygon.technology',        explorer: 'https://amoy.polygonscan.com',   chainId: 80002,    name: 'Polygon Amoy',       symbol: 'MATIC',color: '#8247E5' },
    base:     { rpc: 'https://sepolia.base.org',                   explorer: 'https://sepolia.basescan.org',   chainId: 84532,    name: 'Base Sepolia',       symbol: 'ETH',  color: '#0052FF' },
    solana:   { rpc: 'https://api.devnet.solana.com',              explorer: 'https://explorer.solana.com/?cluster=devnet', name: 'Solana Devnet', symbol: 'SOL', color: '#9945FF' },
};

/* Get active RPC for a network — respects testnet mode */
export function getNetworkRpc( netKey ) {
    const isTestnet = localStorage.getItem( 'chainvault_testnet_mode' ) === '1';
    if ( isTestnet && TESTNET_NETWORKS[ netKey ] ) return TESTNET_NETWORKS[ netKey ].rpc;
    const fallbacks = {
        ethereum: 'https://ethereum-rpc.publicnode.com',
        bsc:      'https://bsc-dataseed.binance.org',
        polygon:  'https://polygon-bor-rpc.publicnode.com',
        base:     'https://mainnet.base.org',
        solana:   'https://api.mainnet-beta.solana.com',
    };
    return fallbacks[ netKey ] || '';
}

/* Backup RPC endpoints — used if the primary in NETWORKS fails */
const ETH_RPC_FALLBACKS = {
    ethereum: [ 'https://ethereum-rpc.publicnode.com', 'https://rpc.ankr.com/eth', 'https://1rpc.io/eth' ],
    bsc:      [ 'https://bsc-dataseed.binance.org', 'https://bsc-dataseed1.defibit.io', 'https://rpc.ankr.com/bsc' ],
    polygon:  [ 'https://polygon-bor-rpc.publicnode.com', 'https://rpc.ankr.com/polygon', 'https://1rpc.io/matic' ],
    base:     [ 'https://mainnet.base.org', 'https://base.publicnode.com', 'https://1rpc.io/base' ],
};

async function getBalanceWithFallback( walletAddress, netKey ) {
    /* Use testnet RPC if testnet mode is enabled */
    const isTestnet = localStorage.getItem( 'chainvault_testnet_mode' ) === '1';
    if ( isTestnet && TESTNET_NETWORKS[ netKey ] ) {
        const provider = new ethers.JsonRpcProvider( TESTNET_NETWORKS[ netKey ].rpc );
        const balance  = await provider.getBalance( walletAddress );
        return balance;
    }
    const urls = ETH_RPC_FALLBACKS[ netKey ] || [];
    for ( const url of urls ) {
        try {
            const provider = new ethers.JsonRpcProvider( url );
            const balance = await Promise.race( [
                provider.getBalance( walletAddress ),
                new Promise( ( _, rej ) => setTimeout( () => rej( new Error( 'timeout' ) ), 4000 ) )
            ] );
            return balance;
        } catch ( e ) {
            console.warn( '[balance] RPC failed: ' + url, e.message );
        }
    }
    throw new Error( 'All RPC endpoints failed for ' + netKey );
}
export const NETWORKS = {
    ethereum: {
        chainId:  1,
        name:     'Ethereum',
        symbol:   'ETH',
        decimals: 18,
        rpc:      'https://ethereum-rpc.publicnode.com',
        explorer: 'https://etherscan.io',
        icon:     '⟠',
        color:    '#627EEA',
    },
    bsc: {
        chainId:  56,
        name:     'BNB Smart Chain',
        symbol:   'BNB',
        decimals: 18,
        rpc:      'https://bsc-dataseed.binance.org',
        explorer: 'https://bscscan.com',
        icon:     '⬡',
        color:    '#F0B90B',
    },
    polygon: {
        chainId:  137,
        name:     'Polygon',
        symbol:   'MATIC',
        decimals: 18,
        rpc:      'https://polygon-bor-rpc.publicnode.com',
        explorer: 'https://polygonscan.com',
        icon:     '⬡',
        color:    '#8247E5',
    },
    base: {
        chainId:  8453,
        name:     'Base',
        symbol:   'ETH',
        decimals: 18,
        rpc:      'https://mainnet.base.org',
        explorer: 'https://basescan.org',
        icon:     '🔵',
        color:    '#0052FF',
    },
    solana: {
        chainId:  null,
        name:     'Solana',
        symbol:   'SOL',
        decimals: 9,
        rpc:      'https://solana-mainnet.g.alchemy.com/v2/demo',
        explorer: 'https://solscan.io',
        icon:     '◎',
        color:    '#9945FF',
    },
};

export function WalletProvider( { children } ) {

    const [ selectedNetwork,       setSelectedNetwork       ] = useState( 'ethereum' );
    const [ address,               setAddress               ] = useState( null );
    const [ balances,              setBalances              ] = useState( {} );
    const [ prices,                setPrices                ] = useState( {} );
    const [ tokens,                setTokens                ] = useState( [] );
    const [ solTokens,             setSolTokens             ] = useState( [] );
    const [ transactions,          setTransactions          ] = useState( [] );
    const [ isConnecting,          setIsConnecting          ] = useState( false );
    const [ isLoadingBalance,      setIsLoadingBalance      ] = useState( false );
    const [ isLoadingTransactions, setIsLoadingTransactions ] = useState( false );
    const [ theme,                 setTheme                 ] = useState(
        localStorage.getItem( 'chainvault_theme' ) || 'dark'
    );
    const [ testnetMode,           setTestnetMode           ] = useState(
        localStorage.getItem( 'chainvault_testnet_mode' ) === '1'
    );
    const [ walletType,     setWalletType     ] = useState( null );
    const [ builtInWallet,  setBuiltInWallet  ] = useState( null );
    const [ showOnboarding, setShowOnboarding ] = useState( false );

    const walletTypeRef    = useRef( null );
    const mmProviderRef    = useRef( null );
    const builtInWalletRef = useRef( null );
    const balancesRef      = useRef( {} );

    useEffect( () => { walletTypeRef.current    = walletType;    }, [ walletType    ] );
    useEffect( () => { builtInWalletRef.current = builtInWallet; }, [ builtInWallet ] );
    useEffect( () => { balancesRef.current      = balances;      }, [ balances      ] );

    useEffect( () => {
        if ( walletExistsInStorage() ) {
            const stored = loadWalletFromStorage();
            if ( stored ) {
                try {
                    if ( window?.solana?.isConnected ) {
                        window.solana.disconnect();
                    }
                } catch ( e ) { /* ignore */ }

                /* Load active account — not always account 1 */
                const activeIdx   = parseInt( localStorage.getItem( 'chainvault_active_account' ) || '0' );
                const storedAccts = localStorage.getItem( 'chainvault_accounts' );
                const accounts    = storedAccts ? JSON.parse( storedAccts ) : [];
                const activeAcct  = accounts[ activeIdx ];

                /* Build wallet state using active account addresses */
                const activeWallet = activeAcct ? {
                    ...stored,
                    ethereum: {
                        ...stored.ethereum,
                        address:    activeAcct.ethereumAddress || stored.ethereum.address,
                        privateKey: activeAcct.privateKey      || stored.ethereum.privateKey,
                    },
                    solana: {
                        ...stored.solana,
                        address:   activeAcct.solanaAddress   || stored.solana.address,
                        secretKey: activeAcct.solanaSecretKey
                            ? new Uint8Array( activeAcct.solanaSecretKey )
                            : stored.solana.secretKey,
                    },
                } : stored;

                setBuiltInWallet( activeWallet );
                setAddress( activeAcct?.ethereumAddress || stored.ethereum.address );
                setWalletType( 'builtin' );
                setSelectedNetwork( 'ethereum' );
                setShowOnboarding( false );

                /* Fetch all balances on startup including SOL */
                const solAddr = activeAcct?.solanaAddress || stored.solana?.address;
                const ethAddr = activeAcct?.ethereumAddress || stored.ethereum.address;
                setTimeout( () => {
                    if ( solAddr ) fetchSolanaBalance( solAddr, true );
                    if ( ethAddr ) fetchAllEvmBalances( ethAddr, true );
                }, 500 );
            }
        } else {
            setShowOnboarding( true );
        }
    }, [] );

    const toggleTheme = useCallback( () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme( newTheme );
        localStorage.setItem( 'chainvault_theme', newTheme );
    }, [ theme ] );

    const toggleTestnetMode = useCallback( () => {
        const newMode = !testnetMode;
        setTestnetMode( newMode );
        localStorage.setItem( 'chainvault_testnet_mode', newMode ? '1' : '0' );
        /* Clear balances so they refetch on new network */
        setBalances( {} );
        balancesRef.current = {};
        toast.success( newMode ? '🧪 Testnet mode enabled' : '🌐 Mainnet mode enabled' );
        /* Reload balances after short delay */
        setTimeout( () => window.location.reload(), 1000 );
    }, [ testnetMode ] );

    const loadPrices = useCallback( async () => {
        try {
            const data = await fetchAllPrices();
            setPrices( data );
        } catch ( error ) {
            console.error( 'Failed to load prices:', error );
        }
    }, [] );

    const loadTransactions = useCallback( async ( walletAddress, network ) => {
        if ( ! walletAddress ) return;
        setIsLoadingTransactions( true );
        // Do NOT clear transactions immediately — keep showing old ones while loading
        try {
            let txs = [];
            if ( network === 'solana' ) {
                txs = await fetchSolTransactions( walletAddress );
            } else {
                txs = await fetchTransactions( walletAddress, network );
            }
            setTransactions( txs );
        } catch ( error ) {
            console.error( 'Failed to load transactions:', error );
            // Keep existing transactions on error instead of clearing
        } finally {
            setIsLoadingTransactions( false );
        }
    }, [] );

    const fetchEthBalance = useCallback( async ( walletAddress, networkKey, silent = true ) => {
        if ( ! walletAddress ) return;
        setIsLoadingBalance( true );
        try {
            const netKey    = networkKey || selectedNetwork;
            const network   = NETWORKS[ netKey ];
            const balance   = await getBalanceWithFallback( walletAddress, netKey );
            const formatted = ethers.formatEther( balance );
            setBalances( prev => ( {
                ...prev,
                [ netKey ]: parseFloat( formatted ).toFixed( 6 ),
            } ) );
        } catch ( error ) {
            console.error( `Failed to fetch ${ networkKey } balance:`, error );
        } finally {
            setIsLoadingBalance( false );
        }
    }, [ selectedNetwork ] );

    const fetchAllEvmBalances = useCallback( async ( walletAddress, silent = false ) => {
        if ( ! walletAddress ) return;

        const evmNetworks = [ 'ethereum', 'bsc', 'polygon', 'base' ];

        const results = await Promise.allSettled(
            evmNetworks.map( async ( netKey ) => {
                const balance  = await getBalanceWithFallback( walletAddress, netKey );
                return {
                    netKey,
                    balance: parseFloat( ethers.formatEther( balance ) ).toFixed( 6 ),
                };
            } )
        );

        const newBalances = {};
        let receivedCrypto = false;

        results.forEach( result => {
            if ( result.status === 'fulfilled' ) {
                const { netKey, balance } = result.value;
                newBalances[ netKey ] = balance;

                const prev    = balancesRef.current[ netKey ];
                const prevNum = parseFloat( prev || '0' );
                const nextNum = parseFloat( balance );

                if ( ! silent && prev !== undefined && prev !== null && prev !== '0' && prev !== '0.000000' && nextNum > prevNum && prevNum > 0 ) {
                    receivedCrypto = true;
                }
            }
        } );

        setBalances( prev => ( { ...prev, ...newBalances } ) );

        if ( receivedCrypto ) {
            playReceiveSound();
            toast.success( '💰 Crypto received!' );
        }
    }, [] );

    const fetchSolanaBalance = useCallback( async ( publicKey, silent = false ) => {
        if ( ! publicKey ) return;
        setIsLoadingBalance( true );
        try {
            const balance = await fetchSolBalance( publicKey );

            const prev    = balancesRef.current.solana;
            const prevNum = parseFloat( prev || '0' );
            const nextNum = parseFloat( balance );

            if ( ! silent && prev !== undefined && prev !== null && prev !== '0' && prev !== '0.000000' && nextNum > prevNum && prevNum > 0 ) {
                playReceiveSound();
                toast.success( '💰 SOL received!' );
            }

            setBalances( prev => ( { ...prev, solana: balance } ) );
            const splTokens = await fetchSolTokenBalances( publicKey );
            setSolTokens( splTokens );
        } catch ( error ) {
            console.error( 'Failed to fetch SOL balance:', error );
        } finally {
            setIsLoadingBalance( false );
        }
    }, [] );

    const clearWalletState = useCallback( () => {
        setAddress( null );
        setWalletType( null );
        setBuiltInWallet( null );
        setBalances( {} );
        setTransactions( [] );
        setSolTokens( [] );
        setSelectedNetwork( 'ethereum' );
    }, [] );

    const handleBuiltInWalletCreated = useCallback( async ( walletData ) => {
        try {
            if ( window?.solana?.isConnected ) {
                window.solana.disconnect();
            }
        } catch ( e ) { /* ignore */ }

        setBuiltInWallet( walletData );
        setAddress( walletData.ethereum.address );
        setWalletType( 'builtin' );
        setSelectedNetwork( 'ethereum' );
        setShowOnboarding( false );
        setBalances( {} );
        setTransactions( [] );

        /* store account 1 with solanaSecretKey so Rosie can send from it */
        try {
            const existing = localStorage.getItem( 'chainvault_accounts' );
            const accounts = existing ? JSON.parse( existing ) : [];
            if ( accounts.length === 0 ) {
                accounts.push({
                    index:           0,
                    label:           'Account 1',
                    ethereumAddress: walletData.ethereum.address,
                    privateKey:      walletData.ethereum.privateKey,
                    solanaAddress:   walletData.solana.address,
                    solanaSecretKey: Array.from( walletData.solana.secretKey ),
                });
                localStorage.setItem( 'chainvault_accounts', JSON.stringify( accounts ) );
            } else if ( accounts[0] && !accounts[0].solanaSecretKey ) {
                accounts[0].solanaSecretKey = Array.from( walletData.solana.secretKey );
                localStorage.setItem( 'chainvault_accounts', JSON.stringify( accounts ) );
            }
        } catch(e) { console.error('Failed to store account secretKey', e); }

        toast.success( 'Wallet ready!' );

        /* Try to restore accounts from server */
        setTimeout( () => restoreAccountsFromServer( walletData ), 1000 );

        await Promise.all( [
            fetchAllEvmBalances( walletData.ethereum.address, true ),
            fetchSolanaBalance( walletData.solana.address, true ),
        ] );

        loadTransactions( walletData.ethereum.address, 'ethereum' );
        loadPrices();
    }, [ fetchAllEvmBalances, fetchSolanaBalance, loadTransactions, loadPrices ] );

    const connectMetaMask = useCallback( async () => {
        /* Find the real MetaMask provider — not Phantom's EVM injection */
        let mmProvider = null;
        if ( window.ethereum?.providers ) {
            /* Multiple wallets installed — find MetaMask specifically */
            mmProvider = window.ethereum.providers.find( p => p.isMetaMask && !p.isPhantom );
        } else if ( window.ethereum?.isMetaMask && !window.ethereum?.isPhantom ) {
            mmProvider = window.ethereum;
        }

        if ( ! mmProvider ) {
            toast.error( 'MetaMask is not installed. Please install it first.' );
            window.open( 'https://metamask.io', '_blank' );
            return;
        }

        /* Clear any existing built-in wallet state before connecting MetaMask */
        setBuiltInWallet( null );
        setAddress( null );
        setWalletType( null );
        balancesRef.current = {};
        setBalances( {} );

        setIsConnecting( true );

        try {
            /* Revoke existing permissions first so MetaMask shows fresh account selection */
            try {
                await mmProvider.request( {
                    method: 'wallet_revokePermissions',
                    params: [ { eth_accounts: {} } ],
                } );
            } catch(e) { /* ignore if not supported */ }

            /* Force MetaMask to open and show account selection */
            const permissions = await mmProvider.request( {
                method: 'wallet_requestPermissions',
                params: [ { eth_accounts: {} } ],
            } );

            const accounts = await mmProvider.request( {
                method: 'eth_accounts',
            } );

            if ( accounts.length > 0 ) {
                setBalances( {} );
                balancesRef.current = {};

                /* Clear built-in wallet state so MetaMask address shows correctly */
                setBuiltInWallet( null );
                setAddress( accounts[ 0 ] );
                setWalletType( 'metamask' );
                setSelectedNetwork( 'ethereum' );
                setShowOnboarding( false );
                toast.success( 'MetaMask connected successfully!' );

                try {
                    const provider  = new ethers.BrowserProvider( window.ethereum );
                    const balance   = await provider.getBalance( accounts[ 0 ] );
                    const formatted = ethers.formatEther( balance );
                    setBalances( { ethereum: parseFloat( formatted ).toFixed( 6 ) } );
                } catch ( balanceError ) {
                    console.error( balanceError );
                }

                loadTransactions( accounts[ 0 ], 'ethereum' );
                loadPrices();
            }
        } catch ( error ) {
            if ( error.code === 4001 ) {
                toast.error( 'Connection rejected. Please try again.' );
            } else {
                toast.error( 'Failed to connect MetaMask.' );
            }
        } finally {
            setIsConnecting( false );
        }
    }, [ loadTransactions, loadPrices ] );

    const connectPhantomWallet = useCallback( async () => {
        if ( ! isPhantomInstalled() ) {
            toast.error( 'Phantom is not installed. Please install it first.' );
            window.open( 'https://phantom.app', '_blank' );
            return;
        }

        setIsConnecting( true );

        try {
            const publicKey = await connectPhantom();

            if ( publicKey ) {
                setBalances( {} );
                balancesRef.current = {};

                setAddress( publicKey );
                setWalletType( 'phantom' );
                setBuiltInWallet( null );
                setSelectedNetwork( 'solana' );
                setShowOnboarding( false );
                toast.success( 'Phantom connected successfully!' );

                await fetchSolanaBalance( publicKey, true );
                loadTransactions( publicKey, 'solana' );
                loadPrices();
            }
        } catch ( error ) {
            console.error( 'Phantom connection error:', error );
            toast.error( 'Failed to connect Phantom.' );
        } finally {
            setIsConnecting( false );
        }
    }, [ fetchSolanaBalance, loadTransactions, loadPrices ] );

    const disconnectWallet = useCallback( () => {
        if ( walletType === 'builtin' ) {
            deleteWalletFromStorage();
            setShowOnboarding( true );
        }

        balancesRef.current = {};
        clearWalletState();
        toast.success( 'Wallet disconnected.' );

        setTimeout( () => {
            try {
                if ( window?.solana ) {
                    window.solana.disconnect();
                }
                /* Reset MetaMask connection so re-connect prompts account selection */
                if ( window?.ethereum && window.ethereum.request ) {
                    window.ethereum.request({
                        method: 'wallet_revokePermissions',
                        params: [{ eth_accounts: {} }]
                    }).catch( () => {} );
                }
            } catch ( e ) { /* ignore */ }
        }, 100 );
    }, [ walletType, clearWalletState ] );

    const switchNetwork = useCallback( ( networkKey ) => {
        if ( ! NETWORKS[ networkKey ] ) return;

        if ( networkKey === 'solana' && walletType === 'metamask' ) {
            toast.error( 'Connect Phantom wallet to use Solana.' );
            return;
        }

        if ( networkKey !== 'solana' && walletType === 'phantom' ) {
            toast.error( 'Connect MetaMask to use EVM networks.' );
            return;
        }

        setSelectedNetwork( networkKey );
        setTransactions( [] );
        toast.success( `Switched to ${ NETWORKS[ networkKey ].name }` );

        if ( walletType === 'builtin' && builtInWallet ) {
            /* always use active account address not just account 1 */
            const activeIdx     = parseInt( localStorage.getItem( 'chainvault_active_account' ) || '0' );
            const storedAccts   = localStorage.getItem( 'chainvault_accounts' );
            const accounts      = storedAccts ? JSON.parse( storedAccts ) : [];
            const activeAcct    = accounts[ activeIdx ];
            const activeSolAddr = activeAcct?.solanaAddress || builtInWallet.solana.address;
            const activeEthAddr = activeAcct?.ethereumAddress || builtInWallet.ethereum.address;

            if ( networkKey === 'solana' ) {
                fetchSolanaBalance( activeSolAddr, true );
                loadTransactions( activeSolAddr, 'solana' );
            } else {
                fetchEthBalance( activeEthAddr, networkKey, true );
                loadTransactions( activeEthAddr, networkKey );
            }
        } else if ( address ) {
            if ( networkKey === 'solana' ) {
                fetchSolanaBalance( address, true );
                loadTransactions( address, 'solana' );
            } else {
                fetchEthBalance( address, networkKey, true );
                loadTransactions( address, networkKey );
            }
        }
    }, [ address, walletType, builtInWallet, fetchEthBalance, fetchSolanaBalance, loadTransactions ] );

    const getCurrentAddress = useCallback( () => {
        if ( walletType !== 'builtin' ) return address;
        if ( ! builtInWallet ) return null;
        switch ( selectedNetwork ) {
            case 'solana':  return builtInWallet.solana.address;
            case 'bitcoin': return builtInWallet.bitcoin.address;
            default:        return builtInWallet.ethereum.address;
        }
    }, [ walletType, builtInWallet, selectedNetwork, address ] );

    /**
     * switchAccount — switches the active account and updates all wallet state
     * Updates the address shown in navbar and all balance fetches
     */
    /* ── Save account count to server ── */
    const saveAccountCount = async ( address, count ) => {
        try {
            const apiBase = window.chainvaultSettings?.siteUrl || '/wp-json/chainvault/v1';
            await fetch( apiBase + '/account-count', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, count }),
            });
        } catch(e) {}
    };

    /* ── Restore accounts from server after reimport ── */
    const restoreAccountsFromServer = async ( walletData ) => {
        try {
            const apiBase  = window.chainvaultSettings?.siteUrl || '/wp-json/chainvault/v1';
            const ethAddr  = walletData.ethereum.address;
            const mnemonic = walletData.mnemonic;
            if ( !mnemonic ) return;
            const res   = await fetch( apiBase + '/account-count?address=' + encodeURIComponent(ethAddr) );
            const data  = await res.json();
            const count = parseInt( data.count || 1 );
            if ( count <= 1 ) return;
            const accounts = JSON.parse( localStorage.getItem('chainvault_accounts') || '[]' );
            let added = 0;
            for ( let i = 1; i < count; i++ ) {
                if ( accounts[i] ) continue;
                try {
                    const accountMnemonic = mnemonic + ' account' + i;
                    const { deriveEthWallet, deriveSolWallet } = await import('../utils/wallet');
                    const ethW = await deriveEthWallet( accountMnemonic );
                    const solW = await deriveSolWallet( accountMnemonic );
                    accounts.push({
                        index: i, label: 'Account ' + (i+1),
                        ethereumAddress: ethW.address, privateKey: ethW.privateKey,
                        solanaAddress: solW.address, solanaSecretKey: solW.secretKey,
                    });
                    added++;
                } catch(e) { console.error('Derive account error', i, e); }
            }
            if ( added > 0 ) {
                localStorage.setItem( 'chainvault_accounts', JSON.stringify(accounts) );
                toast.success( 'Restored ' + added + ' account(s)!' );
            }
        } catch(e) { console.error('restoreAccountsFromServer error', e); }
    };

    const switchAccount = useCallback( async ( accountData ) => {
        if ( ! accountData ) return;
        try {
            // Save active account index to localStorage so it persists on refresh
            const storedAccts = localStorage.getItem( 'chainvault_accounts' );
            const accounts    = storedAccts ? JSON.parse( storedAccts ) : [];
            const newIdx      = accounts.findIndex( a =>
                a.ethereumAddress === accountData.ethereumAddress ||
                a.solanaAddress   === accountData.solanaAddress
            );
            if ( newIdx !== -1 ) {
                localStorage.setItem( 'chainvault_active_account', newIdx.toString() );
            }

            // Update the built-in wallet with the new account addresses
            const updatedWallet = {
                ...builtInWallet,
                ethereum: {
                    ...builtInWallet.ethereum,
                    address:    accountData.ethereumAddress,
                    privateKey: accountData.privateKey || builtInWallet.ethereum.privateKey,
                },
                solana: {
                    ...builtInWallet.solana,
                    address:   accountData.solanaAddress   || builtInWallet.solana.address,
                    secretKey: accountData.solanaSecretKey ? new Uint8Array( accountData.solanaSecretKey ) : builtInWallet.solana.secretKey,
                },
            };
            setBuiltInWallet( updatedWallet );
            setAddress( accountData.ethereumAddress );
            setBalances( {} );
            setTransactions( [] );
            balancesRef.current = {};

            // Fetch balances for new account
            await Promise.all( [
                fetchAllEvmBalances( accountData.ethereumAddress, true ),
                fetchSolanaBalance( accountData.solanaAddress || builtInWallet.solana.address, true ),
            ] );

            // Load transactions for current network
            if ( selectedNetwork === 'solana' ) {
                loadTransactions( accountData.solanaAddress || builtInWallet.solana.address, 'solana' );
            } else {
                loadTransactions( accountData.ethereumAddress, selectedNetwork );
            }

            toast.success( 'Switched to ' + accountData.label );
        } catch ( err ) {
            console.error( 'Failed to switch account:', err );
            toast.error( 'Failed to switch account.' );
        }
    }, [ builtInWallet, selectedNetwork, fetchAllEvmBalances, fetchSolanaBalance, loadTransactions ] );

    /**
     * sendFromBuiltIn — now accepts networkOverride so AssetList can
     * pass the asset's own network instead of relying on selectedNetwork.
     * This fixes the bug where sending SOL routed through ETH because
     * selectedNetwork was still 'ethereum'.
     */
    const sendFromBuiltIn = useCallback( async ( toAddress, amount, networkOverride ) => {
        if ( walletType !== 'builtin' || ! builtInWallet ) {
            throw new Error( 'No built-in wallet connected' );
        }

        // Use the asset's network if provided, otherwise fall back to selectedNetwork
        const network = networkOverride || selectedNetwork;

        if ( network === 'solana' ) {
            return sendSolTransaction(
                builtInWallet.solana.secretKey,
                toAddress,
                parseFloat( amount ),
                NETWORKS.solana.rpc
            );
        } else {
            return sendEthTransaction(
                builtInWallet.ethereum.privateKey,
                toAddress,
                amount,
                NETWORKS[ network ] ? NETWORKS[ network ].rpc : NETWORKS.ethereum.rpc
            );
        }
    }, [ walletType, builtInWallet, selectedNetwork ] );

    const formatAddress = ( addr ) => {
        if ( ! addr ) return '';
        return `${ addr.slice( 0, 6 ) }...${ addr.slice( -4 ) }`;
    };

    const getUSDValue = useCallback( ( amount, network ) => {
        const coinMap = {
            ethereum: 'ethereum',
            bsc:      'binancecoin',
            polygon:  'matic-network',
            base:     'ethereum',
            solana:   'solana',
        };
        const coinId = coinMap[ network ];
        if ( ! coinId || ! prices[ coinId ] ) return '$0.00';
        const usd = parseFloat( amount ) * prices[ coinId ].usd;
        return `$${ usd.toFixed( 2 ) }`;
    }, [ prices ] );

    useEffect( () => {
        if ( ! window.ethereum ) return;

        const handleAccountsChanged = ( accounts ) => {
            if ( walletTypeRef.current === 'builtin' ) return;
            if ( accounts.length > 0 ) {
                if ( walletTypeRef.current === 'metamask' ) {
                    setAddress( accounts[ 0 ] );
                    fetchEthBalance( accounts[ 0 ], 'ethereum', true );
                }
            } else {
                if ( walletTypeRef.current === 'metamask' ) {
                    clearWalletState();
                    setShowOnboarding( true );
                }
            }
        };

        const handleChainChanged = () => {
            if ( walletTypeRef.current !== 'builtin' ) {
                window.location.reload();
            }
        };

        window.ethereum.on( 'accountsChanged', handleAccountsChanged );
        window.ethereum.on( 'chainChanged',    handleChainChanged );

        return () => {
            window.ethereum.removeListener( 'accountsChanged', handleAccountsChanged );
            window.ethereum.removeListener( 'chainChanged',    handleChainChanged );
        };
    }, [ fetchEthBalance, clearWalletState ] );

    useEffect( () => {
        listenToPhantomEvents(
            ( publicKey ) => {
                if ( walletTypeRef.current === 'builtin' ) return;
                if ( walletTypeRef.current === 'phantom' ) {
                    setAddress( publicKey );
                    fetchSolanaBalance( publicKey, true );
                }
            },
            () => {
                if ( walletTypeRef.current === 'builtin' ) return;
                if ( walletTypeRef.current === 'phantom' ) {
                    clearWalletState();
                    setShowOnboarding( true );
                }
            }
        );
    }, [ fetchSolanaBalance, clearWalletState ] );

    useEffect( () => {
        loadPrices();
        const interval = setInterval( loadPrices, 60000 );
        return () => clearInterval( interval );
    }, [ loadPrices ] );

    useEffect( () => {
        if ( walletType === 'builtin' && builtInWallet ) {
            fetchAllEvmBalances( builtInWallet.ethereum.address, true );
            fetchSolanaBalance( builtInWallet.solana.address, true );
            loadTransactions( builtInWallet.ethereum.address, 'ethereum' );
        }
    }, [ walletType, builtInWallet, fetchAllEvmBalances, fetchSolanaBalance, loadTransactions ] );

    useEffect( () => {
        const interval = setInterval( () => {
            const wType  = walletTypeRef.current;
            const wallet = builtInWalletRef.current;

            if ( wType === 'builtin' && wallet ) {
                fetchAllEvmBalances( wallet.ethereum.address, false );
                fetchSolanaBalance( wallet.solana.address, false );
            }
        }, 30000 );

        return () => clearInterval( interval );
    }, [ fetchAllEvmBalances, fetchSolanaBalance ] );

    useEffect( () => {
        document.body.className = `chainvault-theme-${ theme }`;
    }, [ theme ] );

    const value = {
        selectedNetwork,
        address,
        balances,
        prices,
        tokens,
        solTokens,
        mmProviderRef,
        testnetMode,
        toggleTestnetMode,
        transactions,
        isConnecting,
        isLoadingBalance,
        isLoadingTransactions,
        theme,
        walletType,
        builtInWallet,
        showOnboarding,
        networks:            NETWORKS,
        currentNetwork:      NETWORKS[ selectedNetwork ],
        connectMetaMask,
        connectPhantomWallet,
        disconnectWallet,
        switchNetwork,
        toggleTheme,
        fetchEthBalance,
        fetchAllEvmBalances,
        fetchSolanaBalance,
        loadTransactions,
        loadPrices,
        getUSDValue,
        formatAddress,
        getCurrentAddress,
        switchAccount,
        saveAccountCount,
        restoreAccountsFromServer,
        sendFromBuiltIn,
        handleBuiltInWalletCreated,
        setShowOnboarding,
        setTokens,
        setTransactions,
    };

    return (
        <WalletContext.Provider value={ value }>
            { children }
        </WalletContext.Provider>
    );
}

export function useWallet() {
    const context = useContext( WalletContext );
    if ( ! context ) {
        throw new Error( 'useWallet must be used inside a WalletProvider' );
    }
    return context;
}

export default WalletContext;